import { useState } from "react";

import { api, type Me } from "../api";
import { ErrorNote, Field } from "../ui";

interface LoginResponse {
  ok: boolean;
  totp_required: boolean;
  totp_enrollment_required: boolean;
  user: Me | null;
}

interface EnrollResponse {
  secret: string;
  otpauth_url: string;
  qr_svg: string;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"password" | "totp" | "enroll">("password");
  const [enrollment, setEnrollment] = useState<EnrollResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const response = await api.post<LoginResponse>("/v1/auth/login", { email, password });
      if (response.totp_required) {
        setStage("totp");
      } else if (response.totp_enrollment_required) {
        // doc 03 §2: TOTP is mandatory for NCI roles, so an account without it
        // enrols before it can do anything rather than being nagged later.
        setEnrollment(await api.post<EnrollResponse>("/v1/auth/totp/enroll"));
        setStage("enroll");
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post<LoginResponse>("/v1/auth/totp/verify", { code });
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    // The pack's document treatment: a navy brand panel with the amber rule,
    // and the work beside it. On a phone the panel collapses to a header.
    <div className="flex min-h-full items-stretch">
      <aside className="hidden w-[42%] max-w-xl flex-col justify-between border-r border-ink-700 bg-ink-950 p-10 lg:flex">
        <div>
          <img src="/brand/netcomtv-logo-white.svg" alt="NetComTV" className="h-7 w-auto" />
          <div className="mt-10 h-[3px] w-16 bg-brand" aria-hidden />
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-ink-100 [text-wrap:balance]">
            The distribution network, in one place.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-400">
            Every Outpost checks in every 30 seconds and reconciles itself to a signed
            desired-state document. What you see here is what the fleet is actually doing.
          </p>
        </div>
        <dl className="grid grid-cols-3 gap-4 text-ink-300">
          {[
            ["30 s", "check-in"],
            ["2×", "origins per channel"],
            ["120 s", "rollback window"],
          ].map(([figure, caption]) => (
            <div key={caption} className="border-t border-ink-700 pt-3">
              <dt className="sr-only">{caption}</dt>
              <dd className="figure text-xl text-ink-100">{figure}</dd>
              <dd className="eyebrow-muted mt-0.5 normal-case tracking-normal">{caption}</dd>
            </div>
          ))}
        </dl>
      </aside>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <img src="/brand/netcomtv-logo-white.svg" alt="NetComTV" className="h-6" />
          </div>
          <div className="mb-6">
            <div className="eyebrow">Outpost</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-ink-100">Sign in</h2>
          </div>

          <div className="card p-6">
          {stage === "password" && (
            <form onSubmit={submitPassword} className="space-y-4">
              <Field label="Email">
                <input
                  className="input"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field label="Password">
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
              {error && <ErrorNote error={error} />}
              <button className="btn-primary w-full" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          )}

          {stage === "totp" && (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm text-ink-300">Enter the six-digit code from your authenticator.</p>
              <Field label="Authentication code">
                <input
                  className="input text-center font-mono text-lg tracking-[0.4em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </Field>
              {error && <ErrorNote error={error} />}
              <button className="btn-primary w-full" disabled={busy || code.length < 6}>
                {busy ? "Verifying…" : "Verify"}
              </button>
            </form>
          )}

          {stage === "enroll" && enrollment && (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm text-ink-300">
                This account needs two-factor authentication. Scan the code, then enter the six digits it
                shows.
              </p>
              <div
                className="mx-auto w-44 rounded-md bg-white p-3 [&_svg]:h-full [&_svg]:w-full"
                dangerouslySetInnerHTML={{ __html: enrollment.qr_svg }}
              />
              <div className="rounded-md border border-ink-600 bg-ink-900 p-2 text-center">
                <div className="text-xs text-ink-400">or enter this secret manually</div>
                <code className="select-all font-mono text-xs text-ink-200">{enrollment.secret}</code>
              </div>
              <Field label="Authentication code">
                <input
                  className="input text-center font-mono text-lg tracking-[0.4em]"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </Field>
              {error && <ErrorNote error={error} />}
              <button className="btn-primary w-full" disabled={busy || code.length < 6}>
                {busy ? "Confirming…" : "Confirm and sign in"}
              </button>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
